/**
 * App Settings Utility
 * Stores application settings in Firestore for persistence across deployments
 */

import { db } from './firebase';
import { doc, getDoc, setDoc, onSnapshot, Unsubscribe, type DocumentSnapshot, type FirestoreError } from 'firebase/firestore';

const SETTINGS_DOC_ID = 'app_settings';
const PUBLIC_SETTINGS_DOC_ID = 'public_settings';

// Sales Flow Types
export interface SalesFlowNode {
  id: string;
  type: 'step' | 'decision' | 'action';
  position: { x: number; y: number };
  data: {
    label: string;
    description: string;
    details?: string[];
    color?: string;
    stepNumber?: number;
  };
}

export interface SalesFlowEdge {
  id: string;
  source: string;
  target: string;
  label?: string;
  sourceHandle?: string;
  targetHandle?: string;
}

export interface SalesFlowData {
  nodes: SalesFlowNode[];
  edges: SalesFlowEdge[];
  updatedAt?: Date;
  updatedBy?: string;
}

// Proposal Builder Rate Settings
export interface ProposalRateSettings {
  // Design rates ($/hr)
  pmRate: number;                  // Project management - default $50/hr
  apmRate: number;                 // Assistant PM - default $40/hr
  leadDesignerRate: number;        // Lead designer - default $120/hr
  supportDesignerRate: number;     // Support designer - default $40/hr
  engineeringRate: number;         // Engineering - default $120/hr

  // Production rate
  productionRatePerBdft: number;   // $/bdft - default $2/bdft

  // Overhead
  overheadDailyRate: number;       // $/day - default $2,213/day

  // Install rates ($/day)
  teamLaborDailyRate: number;      // Default $950/day
  craneDailyRate: number;          // Default $1,600/day
  craneHourlyRate: number;         // Crane $/hour (0 = type per job)
  craneOvertimeHourlyRate: number; // Crane OT $/hour (0 = type per job)
  telehandlerDailyRate: number;    // Default $750/day
  scissorLiftDailyRate: number;    // Default $350/day
  basketLiftDailyRate: number;     // Default $1,500/day

  // Travel
  mileageRate: number;             // $/mile - default varies
  perDiemRate: number;             // $/person/day - default $60
  defaultHotelRate: number;        // $/night - default $150
  defaultFuelBudget: number;       // Default $500
  travelDayRate: number;           // $/day - default $600 for travel days

  // Materials
  postBaseUnitCost: number;        // Default $80

  // Profit
  defaultProfitMargin: number;     // Default 40%

  // Reference locations
  productionFacilityAddress: string; // Aspers, PA
  homeBaseAddress: string;          // Franklin, TN

  // Sample timber list CSV format (stored in media)
  sampleTimberListCsvUrl?: string;
}

// Default proposal rate settings
export const DEFAULT_PROPOSAL_RATES: ProposalRateSettings = {
  pmRate: 50,
  apmRate: 40,
  leadDesignerRate: 120,
  supportDesignerRate: 40,
  engineeringRate: 120,
  productionRatePerBdft: 2,
  overheadDailyRate: 2213,
  teamLaborDailyRate: 950,
  craneDailyRate: 1600,
  craneHourlyRate: 0,
  craneOvertimeHourlyRate: 0,
  telehandlerDailyRate: 750,
  scissorLiftDailyRate: 350,
  basketLiftDailyRate: 1500,
  mileageRate: 0.67,
  perDiemRate: 60,
  defaultHotelRate: 150,
  defaultFuelBudget: 500,
  travelDayRate: 600,
  postBaseUnitCost: 80,
  defaultProfitMargin: 40,
  productionFacilityAddress: 'Aspers, PA',
  homeBaseAddress: 'Franklin, TN',
};

// Checklist item configuration (for settings customization)
export interface ChecklistItemConfig {
  id: string;
  label: string;
  category: 'client' | 'scope' | 'design' | 'install' | 'materials';
  required: boolean;
  enabled: boolean;
}

export const DEFAULT_CHECKLIST_ITEMS: ChecklistItemConfig[] = [
  // Client Information
  { id: 'clientAddressConfirmed', label: 'Client address confirmed (for freight/travel)', category: 'client', required: true, enabled: true },
  { id: 'primaryContactVerified', label: 'Primary contact verified', category: 'client', required: true, enabled: true },
  { id: 'siteAccessDetailsNoted', label: 'Site access details noted', category: 'client', required: false, enabled: true },

  // Project Scope
  { id: 'projectTypeConfirmed', label: 'Kit mod or custom project confirmed', category: 'scope', required: true, enabled: true },
  { id: 'timberSpeciesSelected', label: 'Timber species selected', category: 'scope', required: true, enabled: true },
  { id: 'scopeDiscussed', label: 'Approximate square footage/scope discussed', category: 'scope', required: false, enabled: true },

  // Design Requirements
  { id: 'designMeetingCompleted', label: 'Design meeting completed', category: 'design', required: false, enabled: true },
  { id: 'modelDrawingsReceived', label: '3D model/drawings received from arch team', category: 'design', required: true, enabled: true },
  { id: 'engineeringRequirementsIdentified', label: 'Engineering requirements identified', category: 'design', required: false, enabled: true },

  // Install Logistics
  { id: 'siteVisitCompleted', label: 'Site visit completed (or scheduled)', category: 'install', required: false, enabled: true },
  { id: 'equipmentRequirementsAssessed', label: 'Crane/equipment requirements assessed', category: 'install', required: true, enabled: true },
  { id: 'installDurationDiscussed', label: 'Estimated install duration discussed', category: 'install', required: false, enabled: true },

  // Materials
  { id: 'timberListAvailable', label: 'Timber list available (or in progress)', category: 'materials', required: true, enabled: true },
  { id: 'fastenerRequirementsNoted', label: 'Special fastener requirements noted', category: 'materials', required: false, enabled: true },
];

export interface AppSettings {
  metricsLiteApiKey?: string;
  metricsLiteTestLeadId?: string;
  slackBotToken?: string;
  slackChannelId?: string;  // Channel ID (C...) or channel name (e.g. #crm-leads)
  slackSigningSecret?: string;  // Signing secret for verifying Slack webhook requests
  sendblueApiKey?: string;
  sendblueApiSecret?: string;
  /** Sequence.io card issuer integration (staff-only). */
  sequenceEnabled?: boolean;
  sequenceAccessToken?: string;
  sequenceGraphqlAccessToken?: string;
  sequenceRefreshToken?: string;
  sequenceAuth0ClientId?: string;
  sequenceOrganizationId?: string;
  quickbooksConnected?: boolean;
  crmApiKey?: string; // API key for external integrations (Zapier, etc.)
  /** Unified Bearer key for all Mac-mini agent APIs (agentGatewayApi). */
  agentApiKey?: string;
  /** Bearer key for the Fathom meeting-notes agent API (Cloud Function). */
  meetingNotesAgentApiKey?: string;
  /** Bearer key for the marketing intelligence agent API (Cloud Function). */
  marketingAgentApiKey?: string;
  /** Bearer key for the deals export agent API (Cloud Function). */
  dealsAgentApiKey?: string;
  /** Bearer key for the projects export agent API (Cloud Function). */
  projectsAgentApiKey?: string;
  /** Bearer key for the portal activity agent API (Cloud Function). */
  portalActivityAgentApiKey?: string;
  /** Bearer key for the OPS tasks agent API (Cloud Function). */
  opsTasksAgentApiKey?: string;
  /**
   * Firebase Auth / users doc uid for Caesar (isAgent:true). Used by
   * opsTasksAgentApi `selfAssign: true` and Fathom → Caesar task routing.
   */
  agentAssigneeUid?: string;
  /** Optional CEO uid for ops completion / meeting-action notify fan-out. */
  ceoUserId?: string;
  /** Firestore user id the bidding agent authors annotations / synopses as. */
  biddingAgentUserId?: string;
  /** Display name shown on bidding-agent annotations and document synopses. */
  biddingAgentDisplayName?: string;
  /** Display name shown on marketing-agent annotations and document synopses. */
  marketingAgentDisplayName?: string;
  /** Optional key for authenticated getGuide on agentRegistryApi. */
  agentRegistryApiKey?: string;
  /**
   * Immediate wake URL POSTed by travelAgentApi when a pending travel queue doc is created
   * (plan / chat / search / calendar sync). Env fallback: TRAVEL_WAKE_WEBHOOK_URL.
   */
  travelWakeWebhookUrl?: string;
  /** Authorization header value for the travel wake webhook (prefer `Bearer …`). */
  travelWakeWebhookAuthorization?: string;
  /** Cursor travel-planner automation webhook (doorbell after a CRM plan job). */
  travelPlannerWebhookUrl?: string;
  travelPlannerWebhookKey?: string;
  travelPlannerWebhookAuth?: string;
  /**
   * Cursor CRM Grok Bot automation webhook — doorbell after GitHub push enqueues
   * a releaseNotesReviewJobs doc (env: RELEASE_NOTES_WAKE_WEBHOOK_URL).
   */
  releaseNotesWakeWebhookUrl?: string;
  /** Authorization for the release-notes wake webhook (prefer `Bearer …`). */
  releaseNotesWakeWebhookAuthorization?: string;
  releaseNotesWakeWebhookKey?: string;
  /**
   * Shared secret GitHub Actions sends as Bearer to releaseNotesPushWebhook
   * (env: RELEASE_NOTES_PUSH_WEBHOOK_KEY). Also accepts agentApiKey.
   */
  releaseNotesPushWebhookKey?: string;
  googleCalendarClientId?: string; // OAuth Client ID for Google Calendar

  // Engagement Letter / Docs settings
  engagementLogo?: { url: string; name: string }; // Logo for engagement letters
  engagementBackground?: { url: string; name: string }; // Blurred background image
  engagement3DModel?: { url: string; name: string }; // HTML file for 3D model viewer
  engagementDocuments?: Array<{ id: string; name: string; url: string }>; // Client-accessible PDFs
  engagementVideoUrl?: string; // YouTube URL for engagement letter video
  retainerTotalFeeRange?: string; // Text for Total Estimated Fee range (e.g. "$4,500 - $6,000")
  samplePlansImage?: { url: string; name: string }; // Sample plans image for deliverables section

  // Payment Link mappings
  conceptPaymentLinkId?: string;
  retainerPaymentLinkId?: string;

  // AI/Bidding settings
  geminiApiKey?: string; // Gemini API key for bid assistant
  anthropicApiKey?: string; // Anthropic API key for AI advisor
  openaiApiKey?: string; // OpenAI API key for AI advisor
  aiAdvisorProvider?: string; // Last-used AI advisor provider ('gemini' | 'openai' | 'anthropic')
  aiAdvisorModel?: string; // Last-used AI advisor model

  // Sales Flow settings
  salesFlow?: SalesFlowData; // Interactive sales process visualization

  // Proposal Builder settings
  proposalRates?: ProposalRateSettings; // Rate settings for proposal calculations
  proposalChecklistItems?: ChecklistItemConfig[]; // Customizable checklist items

  // Client Portal appearance
  portalOverlayColor?: string;    // Hex color for portal background overlay (default: #ffffff)
  portalOverlayOpacity?: number;  // 0-1 opacity for portal overlay (default: 0.92)
  portalBackgroundImage?: { url: string; name: string }; // Override background image for portal

  /** Public /crew-jobs list hero (legacy single photo). */
  crewBoardListHero?: { url: string; name: string } | null;
  /** Public /crew-jobs list header carousel — up to three stills. */
  crewBoardListHeroes?: Array<{ url: string; name: string } | null> | null;
  /** Public /crew-jobs/:slug raise-page hero. */
  crewBoardRaiseHero?: { url: string; name: string } | null;

  // Project Timeline defaults (public portal "If you started today" feature)
  projectTimelineDefaults?: {
    phases: Array<{
      id: string;
      label: string;
      durationWeeks: number;
      bufferWeeks: number;
      subPhases?: Array<{
        id: string;
        label: string;
        durationWeeks: number;
      }>;
    }>;
  };

  // Engineering firms and engineers (for EOR assignment on task groups)
  engineeringFirms?: Array<{
    id: string;
    name: string;
    engineers: Array<{
      id: string;
      name: string;
      email?: string;
    }>;
  }>;

  /** Optional external link (SOP, spreadsheet) alongside the in-app hand cut vs CNC calculator */
  handCutVsCncComparisonUrl?: string;

  /** Global default for 3D walkthrough recorder on client portals. Default: false (off-by-default). */
  modelWalkthroughDefaultEnabled?: boolean;

  /** Beta: expose the Phased Project mode toggle in the proposal builder. Default: false (off-by-default). */
  phasedProjectsDefaultEnabled?: boolean;

  [key: string]: any; // Allow other settings
}

/** Keys mirrored to `settings/public_settings` for anonymous portal / proposal reads. */
export const PUBLIC_SETTINGS_KEYS = [
  'engagementLogo', 'engagementBackground', 'engagement3DModel',
  'engagementDocuments', 'engagementVideoUrl', 'retainerTotalFeeRange',
  'samplePlansImage', 'portalOverlayColor', 'portalOverlayOpacity',
  'portalBackgroundImage', 'projectTimelineDefaults',
  'conceptPaymentLinkId', 'retainerPaymentLinkId',
  'crewBoardListHero', 'crewBoardListHeroes', 'crewBoardRaiseHero',
] as const;

export type PublicSettingsKey = (typeof PUBLIC_SETTINGS_KEYS)[number];

/**
 * Merge a patch of app_settings fields into `settings/public_settings` (staff-only write).
 * Call after saving branding to `app_settings` so clients see updates even if the Cloud Function sync lags.
 */
export async function mergePublicSettingsSubset(patch: Record<string, unknown>): Promise<void> {
  const pub: Record<string, unknown> = {};
  for (const key of PUBLIC_SETTINGS_KEYS) {
    if (patch[key] !== undefined) pub[key] = patch[key];
  }
  if (Object.keys(pub).length === 0) return;
  await setDoc(doc(db, 'settings', 'public_settings'), pub, { merge: true });
}

/**
 * Public settings — branding/portal appearance only, readable by anonymous users.
 * Reads from settings/public_settings (synced from app_settings by Cloud Function).
 * Falls back to the public-safe subset of app_settings for staff sessions.
 */
export const getPublicSettings = async (): Promise<Partial<AppSettings>> => {
  try {
    const snap = await getDoc(doc(db, 'settings', 'public_settings'));
    if (snap.exists()) {
      return snap.data() as Partial<AppSettings>;
    }
    // Fallback for staff users if public_settings hasn't been seeded yet
    try {
      const fallback = await getDoc(doc(db, 'settings', 'app_settings'));
      if (fallback.exists()) {
        const all = fallback.data() as Record<string, any>;
        const pub: Partial<AppSettings> = {};
        for (const key of PUBLIC_SETTINGS_KEYS) {
          if (all[key] !== undefined) (pub as any)[key] = all[key];
        }
        return pub;
      }
    } catch { /* app_settings not accessible for anon users — expected */ }
    return {};
  } catch (error) {
    console.error('Error getting public settings:', error);
    return {};
  }
};

let settingsCache: AppSettings | null = null;
let settingsListeners: Array<(settings: AppSettings) => void> = [];
let unsubscribeSnapshot: Unsubscribe | null = null;

/**
 * Initialize settings listener - call this once when app starts
 */
export const initializeSettings = (callback?: (settings: AppSettings) => void) => {
  if (callback) {
    settingsListeners.push(callback);
  }

  // If already initialized, return
  if (unsubscribeSnapshot) {
    return;
  }

  const applySnap = (snapshot: DocumentSnapshot) => {
    settingsCache = snapshot.exists() ? snapshot.data() as AppSettings : {};
    settingsListeners.forEach(listener => {
      listener(settingsCache || {});
    });
  };

  const listen = (docId: string): Unsubscribe =>
    onSnapshot(
      doc(db, 'settings', docId),
      applySnap,
      (error: FirestoreError) => {
        if (docId === SETTINGS_DOC_ID) {
          unsubscribeSnapshot = listen(PUBLIC_SETTINGS_DOC_ID);
          return;
        }
        console.error('Error listening to settings:', error);
        settingsCache = {};
        settingsListeners.forEach(listener => {
          listener({});
        });
      },
    );

  unsubscribeSnapshot = listen(SETTINGS_DOC_ID);
};

/**
 * Get settings (from cache or Firestore)
 */
export const getSettings = async (): Promise<AppSettings> => {
  // Return cached if available
  if (settingsCache !== null) {
    return settingsCache;
  }

  try {
    const settingsRef = doc(db, 'settings', SETTINGS_DOC_ID);
    const snapshot = await getDoc(settingsRef);

    if (snapshot.exists()) {
      settingsCache = snapshot.data() as AppSettings;
      return settingsCache;
    }

    // No settings document exists yet
    settingsCache = {};
    return {};
  } catch (error) {
    console.error('Error getting settings:', error);
    return {};
  }
};

/**
 * Update a specific setting
 */
export const updateSetting = async (key: keyof AppSettings, value: any): Promise<void> => {
  try {
    const settingsRef = doc(db, 'settings', SETTINGS_DOC_ID);
    const currentSettings = await getSettings();

    const updatedSettings: AppSettings = {
      ...currentSettings,
      [key]: value,
      updatedAt: new Date(),
    };

    await setDoc(settingsRef, updatedSettings, { merge: true });

    // Update cache
    settingsCache = updatedSettings;

    // Notify listeners
    settingsListeners.forEach(listener => {
      listener(settingsCache || {});
    });
  } catch (error) {
    console.error('Error updating setting:', error);
    throw error;
  }
};

/**
 * Get a specific setting value
 */
export const getSetting = async <T = any>(key: keyof AppSettings, defaultValue?: T): Promise<T | undefined> => {
  const settings = await getSettings();
  return (settings[key] as T) ?? defaultValue;
};

/**
 * Subscribe to settings changes
 */
export const subscribeToSettings = (callback: (settings: AppSettings) => void): () => void => {
  settingsListeners.push(callback);

  // Immediately call with current cache if available
  if (settingsCache !== null) {
    callback(settingsCache);
  }

  // Return unsubscribe function
  return () => {
    settingsListeners = settingsListeners.filter(listener => listener !== callback);
  };
};

/**
 * Get Metrics Lite API Key (with fallback to localStorage for migration)
 */
export const getMetricsLiteApiKey = async (): Promise<string> => {
  // Try Firestore first
  const dbKey = await getSetting<string>('metricsLiteApiKey');
  if (dbKey) {
    return dbKey;
  }

  // Fallback to localStorage (for migration)
  const localKey = localStorage.getItem('metrics_lite_api_key');
  if (localKey) {
    // Migrate to Firestore
    await updateSetting('metricsLiteApiKey', localKey);
    return localKey;
  }

  // Fallback to environment variable
  return process.env.REACT_APP_METRICS_LITE_API_KEY || '';
};

/**
 * Save Metrics Lite API Key
 */
export const saveMetricsLiteApiKey = async (apiKey: string): Promise<void> => {
  await updateSetting('metricsLiteApiKey', apiKey);
  // Also update localStorage for backward compatibility during migration
  localStorage.setItem('metrics_lite_api_key', apiKey);
};

/**
 * Get CRM API Key for external integrations
 */
export const getCrmApiKey = async (): Promise<string> => {
  const dbKey = await getSetting<string>('crmApiKey');
  return dbKey || '';
};

/**
 * Save CRM API Key
 */
export const saveCrmApiKey = async (apiKey: string): Promise<void> => {
  await updateSetting('crmApiKey', apiKey);
};

/**
 * Unified Mac-mini agent API key (agentGatewayApi).
 */
export const getAgentApiKey = async (): Promise<string> => {
  const dbKey = await getSetting<string>('agentApiKey');
  return dbKey || '';
};

export const saveAgentApiKey = async (apiKey: string): Promise<void> => {
  await updateSetting('agentApiKey', apiKey.trim());
};

/**
 * Get Meeting Notes agent API key (Fathom pipeline).
 */
export const getMeetingNotesAgentApiKey = async (): Promise<string> => {
  const dbKey = await getSetting<string>('meetingNotesAgentApiKey');
  return dbKey || '';
};

/**
 * Save Meeting Notes agent API key.
 */
export const saveMeetingNotesAgentApiKey = async (apiKey: string): Promise<void> => {
  await updateSetting('meetingNotesAgentApiKey', apiKey);
};

/**
 * Get Marketing agent API key (daily competitor intelligence).
 */
export const getMarketingAgentApiKey = async (): Promise<string> => {
  const dbKey = await getSetting<string>('marketingAgentApiKey');
  return dbKey || '';
};

/**
 * Save Marketing agent API key.
 */
export const saveMarketingAgentApiKey = async (apiKey: string): Promise<void> => {
  await updateSetting('marketingAgentApiKey', apiKey.trim());
};

/**
 * Get Deals agent API key.
 */
export const getDealsAgentApiKey = async (): Promise<string> => {
  const dbKey = await getSetting<string>('dealsAgentApiKey');
  return dbKey || '';
};

/**
 * Save Deals agent API key.
 */
export const saveDealsAgentApiKey = async (apiKey: string): Promise<void> => {
  await updateSetting('dealsAgentApiKey', apiKey.trim());
};

/**
 * Get Projects agent API key.
 */
export const getProjectsAgentApiKey = async (): Promise<string> => {
  const dbKey = await getSetting<string>('projectsAgentApiKey');
  return dbKey || '';
};

/**
 * Save Projects agent API key.
 */
export const saveProjectsAgentApiKey = async (apiKey: string): Promise<void> => {
  await updateSetting('projectsAgentApiKey', apiKey.trim());
};

/**
 * Get Portal Activity agent API key.
 */
export const getPortalActivityAgentApiKey = async (): Promise<string> => {
  const dbKey = await getSetting<string>('portalActivityAgentApiKey');
  return dbKey || '';
};

/**
 * Save Portal Activity agent API key.
 */
export const savePortalActivityAgentApiKey = async (apiKey: string): Promise<void> => {
  await updateSetting('portalActivityAgentApiKey', apiKey.trim());
};

export const getOpsTasksAgentApiKey = async (): Promise<string> => {
  const dbKey = await getSetting<string>('opsTasksAgentApiKey');
  return dbKey || '';
};

export const saveOpsTasksAgentApiKey = async (apiKey: string): Promise<void> => {
  await updateSetting('opsTasksAgentApiKey', apiKey.trim());
};

/**
 * Get Bidding agent API key.
 */
export const getBiddingAgentApiKey = async (): Promise<string> => {
  const dbKey = await getSetting<string>('biddingAgentApiKey');
  return dbKey || '';
};

/**
 * Save Bidding agent API key.
 */
export const saveBiddingAgentApiKey = async (apiKey: string): Promise<void> => {
  await updateSetting('biddingAgentApiKey', apiKey.trim());
};

export interface BiddingAgentIdentity {
  userId: string;
  displayName: string;
}

/**
 * Identity the bidding agent authors annotations / synopses as.
 * Falls back to a stable system id + label when unset.
 */
export const getBiddingAgentIdentity = async (): Promise<BiddingAgentIdentity> => {
  const settings = await getSettings();
  return {
    userId: settings.biddingAgentUserId || 'bidding-agent',
    displayName: settings.biddingAgentDisplayName || 'Bidding Agent',
  };
};

/** Save the bidding agent identity used for document annotations / synopses. */
export const saveBiddingAgentIdentity = async (identity: Partial<BiddingAgentIdentity>): Promise<void> => {
  if (identity.userId !== undefined) await updateSetting('biddingAgentUserId', identity.userId.trim());
  if (identity.displayName !== undefined) await updateSetting('biddingAgentDisplayName', identity.displayName.trim());
};

/**
 * Get Sendblue API Key
 */
export const getSendblueApiKey = async (): Promise<string> => {
  const dbKey = await getSetting<string>('sendblueApiKey');
  return dbKey || process.env.REACT_APP_SENDBLUE_API_KEY || '';
};

/**
 * Save Sendblue API Key
 */
export const saveSendblueApiKey = async (apiKey: string): Promise<void> => {
  await updateSetting('sendblueApiKey', apiKey);
};

/**
 * Get Sendblue API Secret
 */
export const getSendblueApiSecret = async (): Promise<string> => {
  const dbSecret = await getSetting<string>('sendblueApiSecret');
  return dbSecret || process.env.REACT_APP_SENDBLUE_API_SECRET || '';
};

/**
 * Save Sendblue API Secret
 */
export const saveSendblueApiSecret = async (apiSecret: string): Promise<void> => {
  await updateSetting('sendblueApiSecret', apiSecret);
};

/**
 * Get both Sendblue credentials
 */
export const getSendblueCredentials = async (): Promise<{ apiKey: string; apiSecret: string }> => {
  const [apiKey, apiSecret] = await Promise.all([
    getSendblueApiKey(),
    getSendblueApiSecret(),
  ]);
  return { apiKey, apiSecret };
};

export interface SequenceSyncMeta {
  lastCardSyncAt?: Date;
  lastTransactionSyncAt?: Date;
  lastScheduledSyncAt?: Date;
  lastCardCount?: number;
  lastTransactionCount?: number;
  lastMatchCount?: number;
  lastAssignCount?: number;
  lastError?: string;
}

/**
 * Sequence.io integration settings (card issuer → cardholder mapping).
 */
export const getSequenceSettings = async (): Promise<{
  enabled: boolean;
  accessToken: string;
  graphqlAccessToken: string;
  refreshToken: string;
  auth0ClientId: string;
  organizationId: string;
}> => {
  const settings = await getSettings();
  return {
    enabled: settings.sequenceEnabled === true,
    accessToken: settings.sequenceAccessToken || '',
    graphqlAccessToken: settings.sequenceGraphqlAccessToken || '',
    refreshToken: settings.sequenceRefreshToken || '',
    auth0ClientId: settings.sequenceAuth0ClientId || 'oYIhXhAJH9MCejmnoCjYyUhuYckEjiGV',
    organizationId: settings.sequenceOrganizationId || '',
  };
};

export const saveSequenceSettings = async (params: {
  enabled?: boolean;
  accessToken?: string;
  graphqlAccessToken?: string;
  refreshToken?: string;
  auth0ClientId?: string;
  organizationId?: string;
}): Promise<void> => {
  const patch: Record<string, unknown> = {};
  if (params.enabled !== undefined) patch.sequenceEnabled = params.enabled;
  if (params.accessToken !== undefined) patch.sequenceAccessToken = params.accessToken.trim();
  if (params.graphqlAccessToken !== undefined) patch.sequenceGraphqlAccessToken = params.graphqlAccessToken.trim();
  if (params.refreshToken !== undefined) patch.sequenceRefreshToken = params.refreshToken.trim();
  if (params.auth0ClientId !== undefined) patch.sequenceAuth0ClientId = params.auth0ClientId.trim();
  if (params.organizationId !== undefined) patch.sequenceOrganizationId = params.organizationId.trim();
  if (Object.keys(patch).length === 0) return;
  await setDoc(doc(db, 'settings', 'app_settings'), patch, { merge: true });
  if (settingsCache) {
    settingsCache = { ...settingsCache, ...patch } as AppSettings;
  }
};

export const getSequenceSyncMeta = async (): Promise<SequenceSyncMeta> => {
  try {
    const snap = await getDoc(doc(db, 'settings', 'sequenceSync'));
    if (!snap.exists()) return {};
    const data = snap.data();
    return {
      lastCardSyncAt: data.lastCardSyncAt?.toDate?.() || undefined,
      lastTransactionSyncAt: data.lastTransactionSyncAt?.toDate?.() || undefined,
      lastScheduledSyncAt: data.lastScheduledSyncAt?.toDate?.() || undefined,
      lastCardCount: data.lastCardCount,
      lastTransactionCount: data.lastTransactionCount,
      lastMatchCount: data.lastMatchCount,
      lastAssignCount: data.lastAssignCount,
      lastError: data.lastError,
    };
  } catch {
    return {};
  }
};

/**
 * Get Slack settings for lead comment notifications
 */
export const getSlackSettings = async (): Promise<{ botToken: string; channelId: string }> => {
  const settings = await getSettings();
  return {
    botToken: settings.slackBotToken || process.env.REACT_APP_SLACK_BOT_TOKEN || '',
    channelId: settings.slackChannelId || process.env.REACT_APP_SLACK_CHANNEL_ID || '',
  };
};

/**
 * Save Slack settings
 */
export const saveSlackSettings = async (botToken: string, channelId: string): Promise<void> => {
  await updateSetting('slackBotToken', botToken);
  await updateSetting('slackChannelId', channelId);
};

/**
 * Get Proposal Builder rate settings (with defaults)
 */
export const getProposalRates = async (): Promise<ProposalRateSettings> => {
  const settings = await getSettings();
  return {
    ...DEFAULT_PROPOSAL_RATES,
    ...settings.proposalRates,
  };
};

/**
 * Save Proposal Builder rate settings
 */
export const saveProposalRates = async (rates: Partial<ProposalRateSettings>): Promise<void> => {
  const currentRates = await getProposalRates();
  await updateSetting('proposalRates', {
    ...currentRates,
    ...rates,
  });
};

/**
 * Get Proposal Checklist Items (with defaults)
 */
export const getProposalChecklistItems = async (): Promise<ChecklistItemConfig[]> => {
  const settings = await getSettings();
  return settings.proposalChecklistItems || DEFAULT_CHECKLIST_ITEMS;
};

/**
 * Save Proposal Checklist Items
 */
export const saveProposalChecklistItems = async (items: ChecklistItemConfig[]): Promise<void> => {
  await updateSetting('proposalChecklistItems', items);
};

/**
 * Get Anthropic API Key
 */
export const getAnthropicApiKey = async (): Promise<string> => {
  const dbKey = await getSetting<string>('anthropicApiKey');
  return dbKey || '';
};

/**
 * Save Anthropic API Key
 */
export const saveAnthropicApiKey = async (apiKey: string): Promise<void> => {
  await updateSetting('anthropicApiKey', apiKey);
};

/**
 * Get OpenAI API Key
 */
export const getOpenaiApiKey = async (): Promise<string> => {
  const dbKey = await getSetting<string>('openaiApiKey');
  return dbKey || '';
};

/**
 * Save OpenAI API Key
 */
export const saveOpenaiApiKey = async (apiKey: string): Promise<void> => {
  await updateSetting('openaiApiKey', apiKey);
};

/**
 * Get AI Advisor preferences (last-used provider & model)
 */
export const getAiAdvisorPrefs = async (): Promise<{ provider: string; model: string }> => {
  const settings = await getSettings();
  return {
    provider: settings.aiAdvisorProvider || 'gemini',
    model: settings.aiAdvisorModel || '',
  };
};

/**
 * Save AI Advisor preferences
 */
export const saveAiAdvisorPrefs = async (provider: string, model: string): Promise<void> => {
  await updateSetting('aiAdvisorProvider', provider);
  await updateSetting('aiAdvisorModel', model);
};
